import { ChevronDown, ChevronRight } from 'lucide-react';

import { Switch } from '@/components/ui';
import { cn } from '@/lib/utils';
import { usePreviewMode, usePreviewRank, usePreviewRow } from '@/features/flow-optimizer/components/preview/PreviewIndexContext';
import { PreviewRowHoverCard } from '@/features/flow-optimizer/components/preview/hover';
import PreviewRankBadge from '@/features/flow-optimizer/components/preview/PreviewRankBadge';
import PreviewExcludedIndicator from '@/features/flow-optimizer/components/preview/PreviewExcludedIndicator';
import PreviewWeightCells from '@/features/flow-optimizer/components/preview/PreviewWeightCells';
import { excludedReasonText, showExcludedBadge } from '@/features/flow-optimizer/components/preview/preview-row.types';

import { useFlowRowPreviewLocked } from '../../common/FlowRowPreviewWeight';
import FlowItemStatsPanel from '../FlowItemStatsPanel';
import { FlowPathNameTags } from './FlowPathNameTags';
import { FlowPathsProfitTag, type FlowPathListItem } from './FlowPathsPanel.parts';

type Metrics = Record<string, number>;

interface FlowPathsPathRowProps {
  path: FlowPathListItem;
  isSelected: boolean;
  isExpanded: boolean;
  profit?: number;
  isMutating: boolean;
  metricsRangeLabel?: string;
  pathMetrics?: Metrics;
  onSelect: () => void;
  onToggleExpand: () => void;
  onToggleActive?: (active: boolean) => void;
}

const CHEVRON_BTN =
  '-ml-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted/60 hover:text-foreground';

export function FlowPathsPathRow({
  path,
  isSelected,
  isExpanded,
  profit,
  isMutating,
  metricsRangeLabel,
  pathMetrics,
  onSelect,
  onToggleExpand,
  onToggleActive,
}: FlowPathsPathRowProps) {
  const originalWeight = path.originalWeight ?? path.weight;
  const originalActive = path.originalActive ?? path.active;
  const isDirty = originalWeight !== path.weight || originalActive !== path.active;
  const changedActive = originalActive !== path.active;
  const previewRow = usePreviewRow('paths', path.id);
  const previewMode = usePreviewMode();
  const rank = usePreviewRank('paths', path.id);
  const previewLocked = useFlowRowPreviewLocked(previewRow, path.active);

  const chevron = (
    <button
      type="button"
      className={cn(CHEVRON_BTN, isExpanded && 'text-foreground')}
      onClick={(e) => {
        e.stopPropagation();
        onToggleExpand();
      }}
      aria-label={isExpanded ? 'Hide statistics' : 'Show statistics'}
    >
      {isExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
    </button>
  );

  const pathSwitch = onToggleActive ? (
    <Switch
      checked={path.active}
      disabled={isMutating}
      onCheckedChange={(checked) => onToggleActive(Boolean(checked))}
      onClick={(e) => e.stopPropagation()}
      aria-label={path.active ? 'Disable path' : 'Enable path'}
    />
  ) : null;

  const nameLabel = (
    // Name is mandatory: it keeps its space and the tags sit inline beside it only
    // when they fit — otherwise they collapse to a "+N" badge (hover to see them).
    <FlowPathNameTags
      name={path.name}
      nameClassName={cn(
        'min-w-0 text-xs font-medium leading-tight',
        previewLocked ? 'truncate' : 'whitespace-normal break-words',
      )}
      layer="paths"
      itemId={path.id}
      excluded={
        showExcludedBadge(previewRow, path.active) ? (
          <PreviewExcludedIndicator title={excludedReasonText(previewRow)} />
        ) : null
      }
    />
  );

  return (
    <PreviewRowHoverCard name={path.name} row={previewRow}>
      <div
        className={cn(
          'transition-colors',
          isSelected && 'bg-primary/5',
          isDirty && !isSelected && 'bg-warning/5',
        )}
      >
        <div
          role="button"
          tabIndex={0}
          onClick={onSelect}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') onSelect();
          }}
          className="w-full px-1.5 py-1 outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
        >
          {previewLocked && previewRow ? (
            // Flat grid (mirrors the Flow Optimizer preview): Current/New/Diff are
            // top-level columns so the FlowOverlayMetricLabels header lines up exactly.
            <div className="grid w-full grid-cols-[1.5rem_1.5rem_minmax(0,1fr)_3rem_0.75rem_3rem_3.25rem_2.75rem] items-center gap-x-1.5">
              {previewMode ? <PreviewRankBadge rank={rank} /> : null}
              {chevron}
              {nameLabel}
              <div className="col-span-4 grid grid-cols-[3rem_0.75rem_3rem_3.25rem] items-center gap-x-1.5">
                <PreviewWeightCells row={previewRow} />
              </div>
              <div className="flex items-center justify-end">{pathSwitch}</div>
            </div>
          ) : (
            <div
              className={cn(
                'grid w-full items-center gap-x-1.5',
                previewMode
                  ? 'grid-cols-[1.5rem_auto_minmax(0,1fr)_auto]'
                  : 'grid-cols-[auto_minmax(0,1fr)_auto]',
              )}
            >
              {previewMode ? <PreviewRankBadge rank={rank} /> : null}
              {chevron}
              {nameLabel}
              <div className="flex shrink-0 items-center justify-end gap-x-1.5">
                <FlowPathsProfitTag profit={profit} />
                <div className="max-w-[5rem] shrink-0 text-right text-[11px] leading-tight text-muted-foreground tabular-nums sm:max-w-[6rem]">
                  <div className="tabular-nums">
                    {originalWeight === path.weight ? (
                      <span>{path.weight}%</span>
                    ) : (
                      <span className="text-warning">
                        {originalWeight}% → {path.weight}%
                      </span>
                    )}
                  </div>
                  {changedActive && (
                    <div className="text-warning">
                      {originalActive ? 'Active' : 'Inactive'} →{' '}
                      {path.active ? 'Active' : 'Inactive'}
                    </div>
                  )}
                </div>
                {pathSwitch}
              </div>
            </div>
          )}
        </div>
        <FlowItemStatsPanel open={isExpanded} metrics={pathMetrics} rangeLabel={metricsRangeLabel} />
      </div>
    </PreviewRowHoverCard>
  );
}
